import * as React from 'react';
import { Typography} from '@mui/material';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';

import { useTranslation } from "react-i18next";

var SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];

function abbreviateNumber(number){

    if (!number)
        return "0.0";

    // what tier? (determines SI symbol)
    var tier = Math.max(Math.log10(Math.abs(number)) / 3 | 0, 0);

    // if zero, we don't need a suffix
    if(tier === 0) return number.toFixed(1).toLocaleString();

    // get suffix and determine scale
    var suffix = SI_SYMBOL[tier];
    var scale = Math.pow(10, tier * 3);

    // scale the number
    var scaled = number / scale;

    // format number and add suffix
    return scaled.toFixed(1).toLocaleString() + suffix;
}

export default function CoGenSavingsWithBaseline(props) {

    const {allGridCost, allGridConsumption, allGridCO2,
           gridSolarCost, gridSolarConsumption, gridSolarCO2,
           coGenManualCost , coGenManualConsumption, coGenManualCO2,
           optimizedCost, optimizedConsumption, optimizedCO2,
           dateString, currencySymbol, title}  = props;

    const { t } = useTranslation();

    return (
        <Box display="flex" flexDirection={"column"} height="100%">
            {"title" !== ""? 
                <Typography component="h2" variant="h6" color="primary">
                    {title}
                </Typography>
                :
                null
            }
            <Box flex={0.75}/>
            <Grid container>
                    <Grid item xs={4}>
                        <Typography component="h2" variant="h7" align="center">
                        {currencySymbol + " " + abbreviateNumber(allGridCost - optimizedCost)}
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography component="h2" variant="h7" align="center">
                        {abbreviateNumber(Math.max(0.0,allGridConsumption - optimizedConsumption))}
                        </Typography>
                        <Typography component="h2" variant="subtitle1" align="center">
                        {"MWh"}
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography component="h2" variant="h7" align="center">
                        {abbreviateNumber(Math.max(0.0, allGridCO2 - optimizedCO2)/1000.0)}
                        </Typography>
                        <Typography component="h2" variant="subtitle1" align="center">
                        {"T CO2"}
                        </Typography>
                    </Grid>
                </Grid> 
            
            {
                dateString !== ""?
                <>
                <Box flex={0.75}/>
                <Typography align={"right"} paddingRight={2}>
                    {t("OnDate", {date: dateString, interpolation: { escapeValue: false }})}
                </Typography>
                </>
                :
                null
            }
            <Box flex={0.75}/>
            <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="right"></TableCell>
                        <TableCell align="center">{currencySymbol}</TableCell>
                        <TableCell align="center">MWh</TableCell>
                        <TableCell align="center">T&nbsp;CO2</TableCell>
                    </TableRow>
                </TableHead>                                                        
                <TableBody>
                    <TableRow>
                        <TableCell align="right">{t("Grid")}</TableCell>
                        <TableCell align="center">{abbreviateNumber(allGridCost)}</TableCell>
                        <TableCell align="center">{abbreviateNumber(allGridConsumption)}</TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber(allGridCO2/1000.0)
                            }
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell align="right">{t("Solar")}</TableCell>
                        <TableCell align="center">{abbreviateNumber(gridSolarCost)}</TableCell>
                        <TableCell align="center">{abbreviateNumber(gridSolarConsumption)}</TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber(gridSolarCO2/1000.0)
                            }
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell align="right">{t("Cogeneration (manual)")}</TableCell>
                        <TableCell align="center">{abbreviateNumber(coGenManualCost)}</TableCell>
                        <TableCell align="center">{abbreviateNumber(coGenManualConsumption)}</TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber(coGenManualCO2/1000.0)
                            }
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell align="right">{t("Optimised")}</TableCell>
                        <TableCell align="center">{abbreviateNumber(optimizedCost)}</TableCell>
                        <TableCell align="center">{abbreviateNumber(optimizedConsumption)}</TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber(optimizedCO2/1000.0)
                            }
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell align="right">{t("Net Savings (xWatts)")}</TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber(coGenManualCost - optimizedCost) 
                            } {
                                coGenManualCost? ((1.0 - optimizedCost.toFixed(2) / coGenManualCost.toFixed(2))*100.0).toFixed(2)  + "%" : "0%"
                            }
                        </TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber(coGenManualConsumption - optimizedConsumption) 
                            } {
                                coGenManualConsumption? Math.max(0.0, (1.0 - optimizedConsumption.toFixed(2) / coGenManualConsumption.toFixed(2))*100.0).toFixed(2)  + "%" : "0%"
                            }
                        </TableCell>
                        <TableCell align="center">
                            {
                                abbreviateNumber((coGenManualCO2 - optimizedCO2)/1000.0)
                            } {
                                coGenManualCO2 ? ((1.0 - (optimizedCO2/1000.0).toFixed(2) / (coGenManualCO2/1000.0).toFixed(2))*100.0).toFixed(0)  + "%" : "0%"
                            }
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            </TableContainer>
            <Box flex={1}/>
        </Box>
    )

}